import React, { useEffect, useState } from "react";
import { Plus, ExternalLink, Trash2, Download, Save, Briefcase, Edit3 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import GermanDateInput from "@/components/GermanDateInput";
import { JobDetailDialog } from "@/components/JobDetailDialog";

const STATUS_META = {
  none:      { label: "—",                      cls: "bg-slate-100 text-slate-500 border-slate-200" },
  applied:   { label: "Beworben",               cls: "bg-amber-100 text-amber-900 border-amber-300" },
  interview: { label: "Vorstellungsgespräch",   cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  offer:     { label: "Angebot",                cls: "bg-emerald-200 text-emerald-950 border-emerald-500 font-semibold" },
  reject:    { label: "Absage",                 cls: "bg-red-100 text-red-900 border-red-300" },
};

/**
 * Job Applications tracker (Modul 3 of "Jobcoaching für Kreative"). Editable
 * by both coach/admin and the client themselves.
 *
 * Props:
 *   clientId (optional): if omitted, the panel uses /me/jobs endpoints (TN view)
 *   showExport (bool): show CSV export button (default true for coach view)
 *   context (optional): "avgs" shows "Modul 3 · Bewerbungen" tag, "wb" shows "Bewerbungen" tag. Default: "avgs".
 */
export default function JobApplicationsPanel({ clientId, showExport = true, context = "avgs" }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // job object or {} for new
  const [viewing, setViewing] = useState(null);

  const listUrl = clientId ? `/clients/${clientId}/jobs` : `/me/jobs`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setJobs(data);
      setViewing((v) => (v ? data.find((j) => j.job_id === v.job_id) || null : v));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const remove = async (job) => {
    if (!confirm(`Eintrag „${job.company} – ${job.function}" löschen?`)) return;
    try {
      await api.delete(`/jobs/${job.job_id}`);
      toast.success("Eintrag gelöscht");
      setViewing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const exportCsv = async () => {
    if (!clientId) return;
    try {
      const res = await api.get(`/clients/${clientId}/jobs.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `job_liste.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const counts = jobs.reduce((acc, j) => {
    acc[j.status] = (acc[j.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="border border-slate-200 bg-white mb-6" data-testid="jobs-panel">
      <div className="px-5 py-3 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
            <Briefcase size={11} strokeWidth={1.5} /> {context === "wb" ? "Bewerbungen" : "Modul 3 · Bewerbungen"}
          </div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Job-Track-Liste</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          {["applied", "interview", "offer", "reject"].map((k) => (
            <span key={k} className={`px-2 py-0.5 border ${STATUS_META[k].cls}`}>
              {STATUS_META[k].label}: {counts[k] || 0}
            </span>
          ))}
          {showExport && clientId && (
            <button onClick={exportCsv}
              className="ml-1 px-3 py-1.5 text-[11px] font-medium border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors flex items-center gap-1"
              data-testid="export-jobs-csv-btn">
              <Download size={11} strokeWidth={1.5} /> CSV
            </button>
          )}
          <button onClick={() => setEditing({})}
            className="px-3 py-1.5 text-[11px] font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1"
            data-testid="add-job-btn">
            <Plus size={11} strokeWidth={2} /> Bewerbung
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Lädt…</div>
      ) : jobs.length === 0 ? (
        <div className="p-8 text-center">
          <Briefcase size={24} strokeWidth={1} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600 mb-3">Noch keine Bewerbungen erfasst.</p>
          <button onClick={() => setEditing({})}
            className="px-4 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1 mx-auto"
            data-testid="add-first-job-btn">
            <Plus size={12} strokeWidth={2} /> Erste Bewerbung erfassen
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="text-left px-4 py-2">Funktion</th>
                <th className="text-left px-4 py-2 hidden sm:table-cell">Unternehmen</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Link</th>
                <th className="text-left px-4 py-2 hidden lg:table-cell">Gesendet</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2 hidden lg:table-cell">Notizen</th>
                <th className="text-right px-4 py-2 w-24 sticky right-0 bg-slate-50">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_id} onClick={() => setViewing(j)} className={`border-t border-slate-100 cursor-pointer hover:brightness-[0.97] ${STATUS_META[j.status]?.cls || ""}`}
                    data-testid={`job-row-${j.job_id}`}>
                  <td className="px-4 py-3 font-medium">{j.function}<div className="sm:hidden text-xs font-normal opacity-80">{j.company}</div></td>
                  <td className="px-4 py-3 hidden sm:table-cell">{j.company}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {j.link ? (
                      <a href={j.link.startsWith("http") ? j.link : `https://${j.link}`}
                         target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                         className="text-[#009F9C] hover:underline inline-flex items-center gap-1 text-xs">
                        Stellenangebot <ExternalLink size={11} />
                      </a>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs font-mono">
                    {j.applied_on ? formatDeShort(j.applied_on) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[11px] border ${STATUS_META[j.status]?.cls || ""}`}>
                      {STATUS_META[j.status]?.label || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-600">
                    <div className="max-w-[220px] truncate" data-testid={`job-notes-${j.job_id}`}>{j.notes}</div>
                    {j.cover_letter && <div className="text-[10px] text-[#009F9C] mt-0.5">✓ Anschreiben</div>}
                  </td>
                  <td className="px-4 py-3 text-right sticky right-0 bg-inherit" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex gap-1 bg-white/70">
                      <button onClick={() => setEditing(j)}
                        className="p-1.5 hover:bg-white/60 border border-slate-300"
                        data-testid={`edit-job-${j.job_id}`} title="Bearbeiten">
                        <Edit3 size={12} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => remove(j)}
                        className="p-1.5 hover:bg-red-100 border border-slate-300 text-red-700"
                        data-testid={`delete-job-${j.job_id}`} title="Löschen">
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && editing === null && (
        <JobDetailDialog job={viewing} statusMeta={STATUS_META} onClose={() => setViewing(null)}
          onEdit={() => setEditing(viewing)} onDelete={() => remove(viewing)} onSaved={load} />
      )}
      {editing !== null && (
        <JobDialog
          initial={editing}
          clientId={clientId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function JobDialog({ initial, clientId, onClose, onSaved }) {
  const isEdit = !!initial.job_id;
  const [form, setForm] = useState({
    function: initial.function || "",
    company: initial.company || "",
    link: initial.link || "",
    applied_on: initial.applied_on || "",
    status: initial.status || "none",
    notes: initial.notes || "",
    contact: initial.contact || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.function.trim() || !form.company.trim()) {
      toast.error("Bitte Funktion und Unternehmen ausfüllen");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        function: form.function.trim(),
        company: form.company.trim(),
        link: form.link.trim() || null,
        applied_on: form.applied_on || null,
        status: form.status,
        notes: form.notes.trim() || null,
        contact: form.contact.trim(),
      };
      if (isEdit) {
        await api.patch(`/jobs/${initial.job_id}`, payload);
      } else {
        const url = clientId ? `/clients/${clientId}/jobs` : `/me/jobs`;
        await api.post(url, payload);
      }
      toast.success(isEdit ? "Aktualisiert" : "Bewerbung erfasst");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            {isEdit ? "Bewerbung bearbeiten" : "Neue Bewerbung"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Modul 3 · Job-Track-Liste
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Funktion / Rolle *">
              <input value={form.function} onChange={(e) => set("function", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                placeholder="z. B. Grafikdesigner/-in"
                data-testid="job-function-input" />
            </Field>
            <Field label="Unternehmen *">
              <input value={form.company} onChange={(e) => set("company", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                placeholder="z. B. Studio Musterstadt"
                data-testid="job-company-input" />
            </Field>
          </div>

          <Field label="Ansprechpartner/-in (für die Anrede im Anschreiben)">
            <input value={form.contact} onChange={(e) => set("contact", e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="z. B. Frau Müller"
              data-testid="job-contact-input" />
          </Field>

          <Field label="Link zur Stellenausschreibung">
            <input value={form.link} onChange={(e) => set("link", e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900 font-mono"
              placeholder="https://…"
              data-testid="job-link-input" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Bewerbung gesendet am">
              <GermanDateInput value={form.applied_on || ""} onChange={(e) => set("applied_on", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                data-testid="job-applied-date" />
            </Field>
            <Field label="Status / Antwort">
              <select value={form.status} onChange={(e) => set("status", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                data-testid="job-status-select">
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Notizen (nächste Schritte, Ansprechpartner …)">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
              rows={3}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="z. B. Nach 1 Woche nachhaken · Ansprechpartner: Frau Müller"
              data-testid="job-notes-input" />
          </Field>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Abbrechen</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
            data-testid="save-job-btn">
            <Save size={14} strokeWidth={1.5} /> {saving ? "Speichern…" : "Speichern"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
